import { IonButton, IonMenu, IonMenuToggle} from '@ionic/react';
import { FC, useEffect, useRef, useState } from 'react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { createUseStyles } from 'react-jss';
import { HamburgerMenuIcon } from '../theme/Icons';
import { mediaMinWidth } from '../theme/utils';
import { LoadingPage } from '../LoadingPage';
import { AnonymousNavigationHeader, NavigationHeader } from './NavigationHeader';
import { NavigationContent } from './NavigationContent';
import { NavigationFooter } from './NavigationFooter';
import { NavigationMenuState } from './NavigationMenuState';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { AppPrincipal, isCaremapViewer } from '../auth/AppPrincipal';
import { ReminderBadge } from '../components/ReminderBadge';
import { IntercomState } from '../support/IntercomService';
import { useSubscription } from '@meraki-internal/state';

const width = 375;
const useStyles = createUseStyles({
    menu: {
        '--max-width': `${width}px`,

        [mediaMinWidth.sm]: {
            '--min-width': `${width}px`,
        },

        '&::part(backdrop)': {
            backgroundColor: 'black',
            opacity: '0.55 !important'
        }
    }
});

type INavigationMenuToggle = {
    slot?: 'start' | 'end',
    noAppHeader?: boolean;
};

export const NavigationMenuToggle: FC<INavigationMenuToggle> = ({ slot, noAppHeader }) => {
    const intercomState = useInstance(IntercomState);
    useSubscription(() => intercomState);

    const unreadIntercomCount = intercomState.state.unreadCount;
    let badgeStyle: any = {
        top: 12,
        left: 29,
        position: 'absolute',
        pointerEvents: 'none'
    };

    if (noAppHeader) {
        badgeStyle = {
            ...badgeStyle,
            top: 0,
            left: 28
        };
    }

    // this component will auto-hide if the corresponding menu is disabled
    // https://ionicframework.com/docs/api/menu-toggle
    return (
        <IonMenuToggle slot={slot} menu="navigation-menu">
            <IonButton fill="clear" id="navigation-menu-button">
                <HamburgerMenuIcon color="light" />
            </IonButton>
            {unreadIntercomCount > 0 &&
                <div style={badgeStyle}>
                    <ReminderBadge size="small" />
                </div>
            }
        </IonMenuToggle>
    );
};

const SelectedNavigationSynchrnoizer: React.FC = () => {
    const history = useInstance(HistoryViewModel);
    const navState = useInstance(NavigationMenuState);

    useEffect(() => {
        const checkForSelectedNavItem = () => {
            const { pathname } = history.getCurrentLocation();

            const isOnCarePath = pathname.startsWith('/care/');
            if (isOnCarePath && navState.state.selectedNavItem !== 'care'){
                navState.setSelectedNavItem('care');
            }
        };
        history.listen(checkForSelectedNavItem);
        checkForSelectedNavItem();
    }, [history, navState]);
    return null;
};

export const NavigationMenu: FC<{contentId: string}> = ({ contentId }) => {
    const classes = useStyles();
    const navMenuState = useInstance(NavigationMenuState);

    const isViewer = isCaremapViewer(useInstance(AppPrincipal));

    const closeBtnRef = useRef<HTMLDivElement>(null);

    const [deleting, setDeleting] = useState(false);

    const closeMenu = () => {
        // TODO: shouldn't need to be this complex, either get the correct menu ref or use menuController
        closeBtnRef.current?.click();
    };

    if (deleting) {
        return (
            <LoadingPage showLogo />
        );
    }

    return (
        <IonMenu
            data-id="navigation-menu"
            menuId="navigation-menu"
            contentId={contentId}
            className={classes.menu}
            onIonWillOpen={() => navMenuState.setOpen(true)}
            onIonDidClose={() => navMenuState.setOpen(false)}
        >
            {!isViewer && (
                <NavigationHeader
                    closeBtnRef={closeBtnRef}
                    onDelete={() => setDeleting(true)}
                />
            )}
            {isViewer && (
                <AnonymousNavigationHeader closeBtnRef={closeBtnRef} />
            )}

            <SelectedNavigationSynchrnoizer />

            <NavigationContent
                popoverWidth={width}
                closeMenuHandler={closeMenu}
            />

            <NavigationFooter closeMenu={closeMenu} />
        </IonMenu>
    );
};
